import type { FormGroupContextProps } from '@ksyos/design-system/dist/components/form-group';
import type { JSX } from 'react';
import { Box, EpisodeHeader, FormGroupContext } from '@ksyos/design-system';
import { useNavigate, useParams } from '@ksyos/router';
import { orderBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { IPatient } from '@common/interfaces/patient';
import type { Process } from '@common/interfaces/processes';
import { episodeCategoryIconName } from '@frontend/components/elements/Icons/episodeCategoryIconName';
import { SplitViewTriggerWrapper } from '@frontend/components/elements/SplitViewTriggerWrapper';
import { getPatient, getProcesses } from '@frontend/services/api/patientsApiService';
import { useUiStateContext } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDeepCompareEffect } from '@frontend/utils/hooks/useDeepCompareEffect';
import { useNavigateTo } from '@frontend/utils/hooks/useNavigateTo';
import { useSplitViewContext } from '@frontend/utils/hooks/useSplitViewContext';
import { getSelectedLanguage } from '../../../translate';
import { Loader } from '../../elements/common';
import { PageTemplate } from '../../templates/PageTemplate';
import { PatientAccessReasons } from '../patients/PatientAccessReasons';
import { PatientAvatarBlock } from '../patients/PatientAvatarBlock';
import { PatientActiveInsuranceContextProvider } from '../patients/PatientInsuranceHistoryContext';
import { OsasRenewedAlert } from './customControls/osas/OsasRenewedAlert';
import { PatientEpisodeCategoryContextProvider } from './PatientEpisodeCategoryContextProvider';
import { PatientEpisodeCategoryHeaderTabBar } from './PatientEpisodeCategoryHeaderTabBar';
import { patientEpisodeCategoryTabs } from './patientEpisodeCategoryHeaderTabs';
import { PatientEpisodeCategoryNavigation } from './PatientEpisodeCategoryNavigation';

const formGroupContextProps: FormGroupContextProps = {
    labelWidth: 'one-quarter',
    size: 'medium',
    variant: 'outline',
};

export function PatientEpisodeCategory(): JSX.Element {
    const abortSignal = useAbortSignal();
    const navigate = useNavigate();
    const navigateTo = useNavigateTo();
    const { episodeCategory, patientId } = useParams() as { episodeCategory: EpisodeCategory; patientId: string };
    const { uiState } = useUiStateContext();
    const { isSplitViewOpen, resetSplitView, setIsSplitViewAvailable } = useSplitViewContext();
    const { activeTab } = useParams();
    const selectedPatientEpisodeCategoryTab = useMemo(getPatientEpisodeCategoryTab, [activeTab, episodeCategory]);
    if (!selectedPatientEpisodeCategoryTab) {
        navigateTo('patientCarePath', { activeTab: 'carePath', episodeCategory, patientId });
    }

    const [patient, setPatient] = useState<IPatient>();
    const [saveStatus, setSaveStatus] = useState<'saving' | 'saved'>();
    const [processes, setProcesses] = useState<Process[]>();

    useEffect(() => {
        loadPatientAndProcesses();

        return () => {
            resetSplitView();
        };
    }, []);

    useDeepCompareEffect(() => {
        setIsSplitViewAvailable(selectedPatientEpisodeCategoryTab?.key === 'carePath');
    }, [selectedPatientEpisodeCategoryTab]);

    useDeepCompareEffect(() => {
        // This handles the scenario when the first activity of a process is a draft and is deleted
        if (processes?.length === 0) {
            resetSplitView();
        }
    }, [processes]);

    if (!patient || !processes || !selectedPatientEpisodeCategoryTab) {
        return (
            <Loader
                centered={true}
                large={true}
            />
        );
    }

    return (
        <PatientAccessReasons
            onCancel={() => navigate(-1)}
            onReasonGiven={loadPatientAndProcesses}
            patientId={patient.id}
        >
            <PatientEpisodeCategoryContextProvider
                episodeCategory={episodeCategory}
                patient={patient}
                processes={processes}
                reload={loadPatientAndProcesses}
                reloadPatient={handleReloadPatient}
                setSaveStatus={setSaveStatus}
            >
                <PatientActiveInsuranceContextProvider patientId={patient.id}>
                    <PageTemplate
                        noGutterOnMobile={true}
                        sideBarNavigation={<PatientEpisodeCategoryNavigation />}
                        sideBarProfile={
                            <PatientAvatarBlock
                                patient={patient}
                                size="large"
                            />
                        }
                        topBarNavigation={<PatientEpisodeCategoryNavigation />}
                        topBarProfile={
                            <PatientAvatarBlock
                                patient={patient}
                                size="small"
                            />
                        }
                    >
                        <OsasRenewedAlert />
                        <FormGroupContext.Provider value={formGroupContextProps}>
                            <EpisodeHeader
                                autoSaveStatus={getEpisodeHeaderStatus(saveStatus)}
                                bannerHeight={uiState.bannerHeight}
                                icon={episodeCategoryIconName(episodeCategory)}
                                id="episode-header"
                                title={getSelectedLanguage().episodeCategoryEnum[episodeCategory]}
                                withinScrollContainer={isSplitViewOpen}
                            >
                                <Box
                                    alignItems="flex-end"
                                    display="flex"
                                >
                                    <PatientEpisodeCategoryHeaderTabBar
                                        selectedPatientEpisodeCategoryTab={selectedPatientEpisodeCategoryTab}
                                    />
                                    <SplitViewTriggerWrapper />
                                </Box>
                            </EpisodeHeader>
                            <selectedPatientEpisodeCategoryTab.component />
                        </FormGroupContext.Provider>
                    </PageTemplate>
                </PatientActiveInsuranceContextProvider>
            </PatientEpisodeCategoryContextProvider>
        </PatientAccessReasons>
    );

    function getPatientEpisodeCategoryTab() {
        if (!activeTab) {
            return undefined;
        }

        return patientEpisodeCategoryTabs.find(
            (patientEpisodeCategoryTab) => patientEpisodeCategoryTab.key === activeTab
        );
    }

    async function getPatientWithAuthorizations(): Promise<IPatient> {
        return getPatient(
            patientId,
            {
                includeItemAuthorizations: [
                    'canSeePatientDetails',
                    'canUpdate',
                    'canUpdatePatientConsent',
                    'canSeeProfessionalSummaryDataDetails',
                    'canSeePatientAllergies',
                    'canSeePatientEpisodes',
                    'canSeePatientLabResults',
                    'canSeePatientMedication',
                ],
            },
            abortSignal
        );
    }

    async function loadPatientAndProcesses(): Promise<void> {
        // [CS-20077] load patient and processes then set state simultaneously to avoid excessive jitter on rerender
        const [nextPatient, patientProcesses] = await Promise.all([
            getPatientWithAuthorizations(),
            getProcesses(patientId, { episodeCategory }, abortSignal),
        ]);
        const orderedProcesses = orderBy(patientProcesses, 'startedDatetime', 'desc');
        setPatient(nextPatient);
        setProcesses(orderedProcesses);
        setSaveStatus(undefined);
    }

    async function handleReloadPatient(): Promise<void> {
        const nextPatient = await getPatientWithAuthorizations();
        setPatient(nextPatient);
    }

    function getEpisodeHeaderStatus(
        status: 'saving' | 'saved' | undefined
    ): { state: 'saving' | 'saved'; text: string } | undefined {
        if (status === 'saving') {
            return { state: 'saving', text: getSelectedLanguage().common.saving };
        }
        if (status === 'saved') {
            return { state: 'saved', text: getSelectedLanguage().common.saved };
        }
    }
}
